import React, { useState } from "react";
import {
    Box,
    CircularProgress,
    MenuItem,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from "@mui/material";

import CampoEdicaoInline from "./CampoEdicaoInline";
import { BORDA_SUAVE_CLARA, NAVY, formatarMoeda } from "./utils";

const SelectTipoServico = ({ servico, tiposServico, disabled, onSalvarCampo }) => {
    const [salvando, setSalvando] = useState(false);
    const [erro, setErro] = useState("");

    async function handleChange(event) {
        setSalvando(true);
        setErro("");
        try {
            await onSalvarCampo(servico, { tipoServicoPortalId: Number(event.target.value) });
        } catch (e) {
            setErro(e?.response?.data?.mensagem || "Erro ao salvar. Tente novamente.");
        } finally {
            setSalvando(false);
        }
    }

    return (
        <>
            <TextField
                select
                size="small"
                value={servico.tipoServicoId ?? ""}
                onChange={handleChange}
                disabled={disabled || salvando}
                sx={{ minWidth: "10rem", backgroundColor: "white" }}
                inputProps={{ "aria-label": `Tipo de serviço do faturamento ${servico.faturamentoId}` }}
                InputProps={salvando ? { endAdornment: <CircularProgress size={14} /> } : undefined}
            >
                {servico.tipoServicoId == null && <MenuItem value="">Selecione...</MenuItem>}
                {tiposServico.map((tipo) => (
                    <MenuItem key={tipo.id} value={tipo.id}>{tipo.tituloTipo}</MenuItem>
                ))}
            </TextField>
            {erro && (
                <Typography variant="caption" color="error" display="block">
                    {erro}
                </Typography>
            )}
        </>
    );
};

const SubgradeServicosBma = ({
    servicos = [],
    tiposServico = [],
    bloqueado = false,
    onSalvarCampo
}) => {
    return (
        <TableContainer
            component={Paper}
            elevation={0}
            sx={{ border: `1px solid ${BORDA_SUAVE_CLARA}`, borderRadius: "12px" }}
        >
            <Table
                size="small"
                sx={{
                    "& .MuiTableCell-head": { fontWeight: 700, backgroundColor: "white", color: NAVY, borderBottom: `1px solid ${BORDA_SUAVE_CLARA}` },
                    "& .MuiTableCell-root": { borderBottom: `1px solid ${BORDA_SUAVE_CLARA}` }
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell>Tipo de Serviço</TableCell>
                        <TableCell>Cód. Serviço SISDM</TableCell>
                        <TableCell>NS</TableCell>
                        <TableCell align="right">Valor</TableCell>
                        <TableCell>Produto</TableCell>
                        <TableCell>Vertical</TableCell>
                    </TableRow>
                </TableHead>

                <TableBody>
                    {servicos.length === 0 && (
                        <TableRow>
                            <TableCell colSpan={6} align="center">
                                <Typography variant="body2" color="text.secondary" sx={{ py: 1.5 }}>
                                    Nenhum serviço encontrado para este cliente.
                                </Typography>
                            </TableCell>
                        </TableRow>
                    )}
                    {servicos.map((servico) => (
                        <React.Fragment key={servico.faturamentoId}>
                            <TableRow hover sx={{ "& > td": { borderBottom: "none" } }}>
                                <TableCell>
                                    <SelectTipoServico
                                        servico={servico}
                                        tiposServico={tiposServico}
                                        disabled={bloqueado}
                                        onSalvarCampo={onSalvarCampo}
                                    />
                                </TableCell>
                                <TableCell>
                                    <CampoEdicaoInline
                                        valor={servico.codigoServicoSisdm}
                                        disabled={bloqueado}
                                        ariaLabel={`Código de serviço SISDM do faturamento ${servico.faturamentoId}`}
                                        onSalvar={(valor) => onSalvarCampo(servico, { servicoSISDM: valor })}
                                    />
                                </TableCell>
                                <TableCell>
                                    <CampoEdicaoInline
                                        valor={servico.numeroSerie}
                                        tipo="texto"
                                        disabled={bloqueado}
                                        ariaLabel={`NS do faturamento ${servico.faturamentoId}`}
                                        onSalvar={(valor) => onSalvarCampo(servico, { numeroSerie: valor })}
                                    />
                                </TableCell>
                                <TableCell align="right">{formatarMoeda(servico.valor)}</TableCell>
                                <TableCell>{servico.produto || "—"}</TableCell>
                                <TableCell>{servico.vertical || "—"}</TableCell>
                            </TableRow>

                            <TableRow>
                                <TableCell colSpan={6} sx={{ pt: 0, pb: 0.5 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Observação de NF:
                                        </Typography>
                                        <CampoEdicaoInline
                                            valor={servico.observacaoNotaFiscal}
                                            tipo="texto"
                                            permitirVazio
                                            disabled={bloqueado}
                                            ariaLabel={`Observação de NF do faturamento ${servico.faturamentoId}`}
                                            onSalvar={(valor) => onSalvarCampo(servico, { observacaoNotaFiscal: valor })}
                                        />
                                    </Box>
                                </TableCell>
                            </TableRow>

                            <TableRow>
                                <TableCell colSpan={6} sx={{ pt: 0 }}>
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                        <Typography variant="caption" color="text.secondary">
                                            Observação de Faturamento:
                                        </Typography>
                                        <Typography variant="body2">
                                            {servico.observacaoFaturamento || "—"}
                                        </Typography>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        </React.Fragment>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default React.memo(SubgradeServicosBma);
